import { useEffect, useState } from "react";
import { Instagram, Play } from "lucide-react";
import { api } from "@/lib/api";

const HANDLE = "youthmodelmanagement";
const PROFILE = `https://www.instagram.com/${HANDLE}/`;
const PHOTO_MS = 6000;

export const mediaUrl = (url) => `${process.env.REACT_APP_BACKEND_URL ?? ""}${url}`;

const fromUploads = (list) => (Array.isArray(list) ? list : []).map((x) => ({ id: x.id, kind: x.kind, src: mediaUrl(x.url), link: PROFILE }));
const fromInstagram = (list) => (Array.isArray(list) ? list : []).filter((x) => x?.media_url)
  .map((x) => ({ id: x.id, kind: "video", src: x.media_url, poster: x.thumbnail_url, link: x.permalink || PROFILE }));

const Thumb = ({ it, i, active, onPick }) => (
  <button type="button" onClick={() => onPick(i)} data-testid={`reel-thumb-${i}`} aria-label={`Show item ${i + 1}`}
    className={`relative aspect-[9/16] w-20 shrink-0 overflow-hidden bg-black/40 transition-[opacity,transform] duration-300 md:w-24 ${active ? "opacity-100 ring-2 ring-white" : "opacity-60 hover:-translate-y-1 hover:opacity-100"}`}>
    {it.kind === "image" || it.poster
      ? <img src={it.poster || it.src} alt="" loading="lazy" className="h-full w-full object-cover" />
      : <video src={it.src} muted playsInline preload="metadata" className="h-full w-full object-cover" />}
    {it.kind === "video" && <Play className="absolute bottom-1.5 left-1.5 h-3.5 w-3.5 text-white" />}
  </button>
);

const Stage = ({ it, single, onEnded }) => {
  useEffect(() => {
    if (it.kind !== "image" || single) return undefined;
    const t = setTimeout(onEnded, PHOTO_MS);
    return () => clearTimeout(t);
  }, [it, single, onEnded]);
  return it.kind === "image"
    ? <img key={it.id} src={it.src} alt="Youth Management" data-testid="reel-image" className="absolute inset-0 h-full w-full object-cover" />
    : <video key={it.id} data-testid="reel-video" src={it.src} poster={it.poster} autoPlay muted playsInline loop={single} onEnded={onEnded}
        className="absolute inset-0 h-full w-full object-cover" />;
};

export const ReelBanner = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([api.get("/site/banner"), api.get("/instagram/reels")]).then(([up, ig]) => {
      if (!alive) return;
      const uploads = up.status === "fulfilled" ? fromUploads(up.value.data) : [];
      const reels = ig.status === "fulfilled" ? fromInstagram(ig.value.data?.reels) : [];
      setItems([...uploads, ...reels]);
    });
    return () => { alive = false; };
  }, []);

  if (!items.length) return null;
  const it = items[idx] ?? items[0];
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section data-testid="reel-banner" className="relative h-[88vh] min-h-[600px] w-full overflow-hidden bg-[#1c1418]">
      <Stage it={it} single={items.length === 1} onEnded={next} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
      <div className="relative flex h-full flex-col justify-between p-5 text-white md:p-10">
        <a href={PROFILE} target="_blank" rel="noopener noreferrer" data-testid="reel-profile-link" className="flex w-fit items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] hover:underline">
          <Instagram className="h-4 w-4 text-[#e9458f]" /> Reels · @{HANDLE}
        </a>
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <h2 className="font-display text-6xl uppercase leading-[0.85] md:text-8xl">On the reel.</h2>
            <a href={it.link} target="_blank" rel="noopener noreferrer" data-testid="reel-open-instagram"
              className="mt-8 inline-flex items-center gap-3 bg-white px-6 py-3.5 font-mono text-xs uppercase tracking-[0.25em] text-[#1c1418] transition-colors hover:bg-[#f4f4f4]">
              <Play className="h-4 w-4 text-[#e9458f]" /> Watch on Instagram
            </a>
          </div>
          {items.length > 1 && (
            <div data-testid="reel-strip" className="-mx-5 flex gap-3 overflow-x-auto px-5 pb-1 md:mx-0 md:max-w-[50%] md:px-0">
              {items.map((x, i) => <Thumb key={x.id} it={x} i={i} active={i === idx} onPick={setIdx} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
